import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  useRadio,
  useRadioGroup,
  Text,
  Wrap,
  WrapItem,
  useColorMode,
  useToken,
} from "@chakra-ui/react";
import { useController } from "react-hook-form";

export function RadioButtonGroup({
  name,
  label,
  control,
  options,
  helperText,
  size = "md",
  isRequired = false,
  isRequiredMessage = "Please select an option",
  onChange: onChangeProp,
  ...rest
}) {
  const {
    field: { value, onChange, ref },
    formState: { errors },
  } = useController({
    control,
    name,
    rules: {
      required: { value: isRequired, message: isRequiredMessage },
    },
  });

  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value: value,
    onChange: onChangeProp || onChange,
  });

  const group = getRootProps();

  return (
    <FormControl
      as="fieldset"
      isInvalid={!!errors[name]}
      isRequired={isRequired}
      {...rest}
    >
      <FormLabel as="legend">{label}</FormLabel>
      <Wrap {...group}>
        {options.map(({ label, value }, i) => {
          const radio = getRadioProps({ value });
          return (
            <RadioButton
              key={value}
              size={size}
              {...radio}
              ref={i === 0 ? ref : undefined}
            >
              {label}
            </RadioButton>
          );
        })}
      </Wrap>
      {errors[name] ? (
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      ) : (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

const RadioButton = React.forwardRef(({ size, ...props }, ref) => {
  const { colorMode } = useColorMode();
  const [brand500] = useToken("colors", ["brand.500"]);

  const { getInputProps, getRadioProps } = useRadio(props);

  const inputProps = getInputProps();
  const radioProps = getRadioProps();

  return (
    <WrapItem>
      <input {...inputProps} ref={ref} />
      <Box
        as="label"
        htmlFor={inputProps.id}
        {...radioProps}
        ref={ref}
        cursor="pointer"
        size="md"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="sm"
        borderColor={colorMode === "light" ? "gray.200" : "whiteAlpha.300 "}
        _checked={{
          bg: colorMode === "light" ? "brand.500" : "brand.200",
          color: colorMode === "light" ? "white" : "gray.900",
          borderColor: colorMode === "light" ? "brand.500" : "whiteAlpha.300",
          _hover: {
            bg: colorMode === "light" ? "brand.600" : "brand.300",
            color: colorMode === "light" ? "white" : "gray.900",
          },
        }}
        _focus={{
          borderColor: brand500,
          boxShadow: `0 0 0 1px ${brand500}`,
        }}
        _hover={{
          bg: colorMode === "light" ? "brand.50" : "rgba(129, 230, 217, 0.12)", // pulled from devtools on teal darkmode button hover
        }}
        px={size === "sm" ? 2 : 3}
        py={size === "sm" ? 1 : 2}
      >
        <Text fontSize={size === "sm" ? "sm" : "md"}>{props.children}</Text>
      </Box>
    </WrapItem>
  );
});
